import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Menu, X } from "lucide-react";
import { Button } from "./Button";

const teams = [
  { label: "Marketing & Creative", to: "/growth", desc: "Content, campaigns, creative, and the AI agents that turn attention into customers." },
  { label: "Workflow & Automation", to: "/workflow", desc: "AI agents for the back-office work that repeats." },
  { label: "Product & Engineering", to: "/product", desc: "Custom products, built and taken to market." },
];
const solutions = [
  { label: "Real Estate", to: "/real-estate" },
  { label: "Healthcare", to: "/healthcare" },
  { label: "Legal", to: "/legal" },
  { label: "Automotive", to: "/automotive" },
  { label: "Home Services", to: "/home-services" },
];
const company = [
  { label: "About us", to: "/about" },
  { label: "Our brands", to: "/our-brands" },
  { label: "Careers", to: "/careers" },
  { label: "Contact", to: "/contact" },
];

const LIGHT_HERO_ROUTES = ["/audit"];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(null);
  const [mobile, setMobile] = useState(false);
  const location = useLocation();
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobile(false);
    setOpen(null);
  }, [location.pathname]);

  const enter = (key) => {
    clearTimeout(closeTimer.current);
    setOpen(key);
  };
  const leave = () => {
    closeTimer.current = setTimeout(() => setOpen(null), 120);
  };

  const dark = scrolled || LIGHT_HERO_ROUTES.includes(location.pathname);
  const linkColor = dark ? "text-[var(--ink)]" : "text-white";

  const Dropdown = ({ id, label, items }) => (
    <div
      className="relative"
      onMouseEnter={() => enter(id)}
      onMouseLeave={leave}
      data-testid={`nav-${id}`}
    >
      <button
        className={`flex items-center gap-1 text-sm font-medium ${linkColor} hover:text-[var(--ember)] transition-colors`}
      >
        {label}
        <ChevronDown
          size={15}
          className={`transition-transform duration-300 ${open === id ? "rotate-180" : ""}`}
        />
      </button>
      <div
        className={`absolute left-1/2 top-full -translate-x-1/2 pt-4 transition-all duration-200 ${
          open === id
            ? "opacity-100 visible translate-y-0"
            : "opacity-0 invisible -translate-y-1 pointer-events-none"
        }`}
      >
        <div className="min-w-[230px] rounded-2xl border border-black/5 bg-[var(--bg-base)] p-2 shadow-[0_20px_50px_-20px_rgba(20,18,16,0.35)]">
          {items.map((it) => (
            <Link
              key={it.label}
              to={it.to}
              className="block rounded-xl px-4 py-2.5 text-sm text-[var(--ink)] hover:bg-[var(--bg-contrast)] hover:text-[var(--ember)] transition-colors"
              data-testid={`nav-link-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <span className="block font-medium">{it.label}</span>
              {it.desc && (
                <span className="mt-0.5 block max-w-[240px] text-[12px] leading-snug text-[var(--muted)]">
                  {it.desc}
                </span>
              )}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        dark
          ? "bg-[var(--bg-base)]/80 backdrop-blur-xl border-b border-black/5"
          : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
        <Link
          to="/"
          className={`flex items-center ${linkColor}`}
          data-testid="nav-logo"
        >
          <img
            src="/theaidept-logo.png"
            alt="The AI Dept."
            className={`h-[50px] w-auto transition-[filter] duration-300 ${dark ? "invert" : ""}`}
          />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          <Dropdown id="teams" label="AI Teams" items={teams} />
          <Dropdown id="solutions" label="Solutions" items={solutions} />
          <Link
            to="/pricing"
            className={`text-sm font-medium ${linkColor} hover:text-[var(--ember)] transition-colors`}
            data-testid="nav-pricing"
          >
            Pricing
          </Link>
          <Link
            to="/labs"
            className={`text-sm font-medium ${linkColor} hover:text-[var(--ember)] transition-colors`}
            data-testid="nav-labs"
          >
            Labs
          </Link>
          <Dropdown id="company" label="Company" items={company} />
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <Button href="https://hq.theaidept.com/" variant="primary" data-testid="nav-demo-cta">
            Client login
          </Button>
        </div>

        <button
          className={`lg:hidden ${linkColor}`}
          onClick={() => setMobile((m) => !m)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {mobile ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {mobile && (
        <div
          className="lg:hidden border-t border-black/5 bg-[var(--bg-base)] px-6 py-6"
          data-testid="nav-mobile-menu"
        >
          <p className="mb-2 text-xs uppercase tracking-[0.12em] text-[var(--muted)]">
            AI Teams
          </p>
          {teams.map((t) => (
            <Link
              key={t.to}
              to={t.to}
              className="block py-2 text-base text-[var(--ink)]"
            >
              {t.label}
            </Link>
          ))}
          <p className="mb-2 mt-5 text-xs uppercase tracking-[0.12em] text-[var(--muted)]">
            Solutions
          </p>
          {solutions.map((s) => (
            <Link
              key={s.label}
              to={s.to}
              className="block py-2 text-base text-[var(--ink)]"
            >
              {s.label}
            </Link>
          ))}
          <p className="mb-2 mt-5 text-xs uppercase tracking-[0.12em] text-[var(--muted)]">
            Company
          </p>
          {company.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              className="block py-2 text-base text-[var(--ink)]"
            >
              {c.label}
            </Link>
          ))}
          <p className="mb-2 mt-5 text-xs uppercase tracking-[0.12em] text-[var(--muted)]">
            More
          </p>
          <Link to="/pricing" className="block py-2 text-base text-[var(--ink)]" data-testid="nav-mobile-pricing">
            Pricing
          </Link>
          <Link to="/labs" className="block py-2 text-base text-[var(--ink)]" data-testid="nav-mobile-labs">
            Labs
          </Link>
          <div className="mt-6">
            <Button href="https://hq.theaidept.com/" variant="primary" className="w-full">
              Client login
            </Button>
          </div>
        </div>
      )}
    </header>
  );
};
